/**
 * The phonics a material can teach with.
 *
 * One table of speech sounds, one of the spellings that make them, a bank of
 * words she can picture, and the order they come in. The reading engines,
 * the voice pack, and the check on generated stories all read from here, so
 * a word added once is spoken, pictured, and counted as decodable everywhere.
 *
 * The order follows systematic synthetic phonics: single sounds that can be
 * held first (mmm, sss) because a held sound blends into the next one without
 * a gap, then stop sounds, then two letters making one sound, then clusters,
 * then vowel teams.
 */

import { freshNow, holds, type ItemMemory } from '../memory'

/** A speech sound. `held` sounds can be stretched while a finger rests on them. */
export interface Phoneme {
  id: string
  /** How a teacher would write the sound for a parent: "mmm", "sh", "ee". */
  say: string
  /** A word it appears in, for the voice brief and for parents. */
  as: string
  held: boolean
}

export const PHONEMES: Phoneme[] = [
  { id: 's', say: 'sss', as: 'sun', held: true },
  { id: 'a', say: 'a', as: 'apple', held: true },
  { id: 't', say: 't', as: 'top', held: false },
  { id: 'p', say: 'p', as: 'pig', held: false },
  { id: 'i', say: 'i', as: 'insect', held: true },
  { id: 'n', say: 'nnn', as: 'net', held: true },
  { id: 'm', say: 'mmm', as: 'map', held: true },
  { id: 'd', say: 'd', as: 'dog', held: false },
  { id: 'g', say: 'g', as: 'goat', held: false },
  { id: 'o', say: 'o', as: 'octopus', held: true },
  { id: 'k', say: 'k', as: 'cat', held: false },
  { id: 'e', say: 'e', as: 'egg', held: true },
  { id: 'u', say: 'u', as: 'umbrella', held: true },
  { id: 'r', say: 'rrr', as: 'run', held: true },
  { id: 'h', say: 'h', as: 'hat', held: false },
  { id: 'b', say: 'b', as: 'bat', held: false },
  { id: 'f', say: 'fff', as: 'fan', held: true },
  { id: 'l', say: 'lll', as: 'leg', held: true },
  { id: 'j', say: 'j', as: 'jam', held: false },
  { id: 'v', say: 'vvv', as: 'van', held: true },
  { id: 'w', say: 'w', as: 'web', held: false },
  { id: 'ks', say: 'ks', as: 'box', held: false },
  { id: 'y', say: 'y', as: 'yes', held: false },
  { id: 'z', say: 'zzz', as: 'zip', held: true },
  { id: 'kw', say: 'kw', as: 'queen', held: false },
  { id: 'ch', say: 'ch', as: 'chick', held: false },
  { id: 'sh', say: 'shh', as: 'ship', held: true },
  { id: 'th', say: 'th', as: 'bath', held: true },
  { id: 'ng', say: 'ng', as: 'ring', held: true },
  { id: 'ee', say: 'ee', as: 'tree', held: true },
  { id: 'oo', say: 'oo', as: 'moon', held: true },
  { id: 'ai', say: 'ai', as: 'rain', held: true },
  { id: 'oa', say: 'oa', as: 'boat', held: true },
  { id: 'ar', say: 'ar', as: 'star', held: true },
  { id: 'or', say: 'or', as: 'fork', held: true },
  { id: 'igh', say: 'igh', as: 'night', held: true },
  { id: 'ow', say: 'ow', as: 'cow', held: true },
]

export const PHONEME_BY_ID: Record<string, Phoneme> = Object.fromEntries(PHONEMES.map((p) => [p.id, p]))

/** Every spelling the engines know, and the sound it makes. */
export const GRAPHEMES: Record<string, string> = {
  s: 's', a: 'a', t: 't', p: 'p', i: 'i', n: 'n', m: 'm', d: 'd', g: 'g', o: 'o',
  c: 'k', k: 'k', ck: 'k', e: 'e', u: 'u', r: 'r', h: 'h', b: 'b', f: 'f', ff: 'f',
  l: 'l', ll: 'l', ss: 's', gg: 'g', j: 'j', v: 'v', w: 'w', x: 'ks', y: 'y', z: 'z', zz: 'z',
  qu: 'kw', ch: 'ch', sh: 'sh', th: 'th', ng: 'ng',
  ee: 'ee', oo: 'oo', ai: 'ai', oa: 'oa', ar: 'ar', or: 'or', igh: 'igh', ow: 'ow',
}

/** A rung of the ladder: what she is ready for, in the order she meets it. */
export interface PhonicsLevel {
  n: number
  name: string
  /** Said to the parent, never to her. */
  about: string
}

/**
 * She holds a blending level once she reads this many words of it (or of a
 * harder level) well. The reading path and the blend engine both use it.
 */
export const LEVEL_HOLD = 3

/** Reading a little book this many times is when it has become hers: the path marks it done, and its last page counts the reads. */
export const BOOK_READS = 3

/** The blending ladder. A level is named by a word she reads at it, which says more to a parent than the phonics term; `about` says why it is that level. */
export const LEVELS: PhonicsLevel[] = [
  { n: 1, name: 'Words like sun', about: 'Three-sound words that start with a sound she can stretch, like mmm-a-p. The easiest way in to blending.' },
  { n: 2, name: 'Words like cat', about: 'Three-sound words that start with a quick sound that cannot be stretched, like c-a-t, so she holds the vowel and joins the quick sound on.' },
  { n: 3, name: 'Words like ship', about: 'sh, ch, th, ck and ng: two letters that make one sound, like sh-i-p.' },
  { n: 4, name: 'Words like frog', about: 'Two consonants together, like f-r-o-g and h-a-n-d. Four sounds to hold at once.' },
  { n: 5, name: 'Words like moon', about: 'Two vowels or a vowel and r making one long sound, like m-oo-n and c-ar.' },
]

/** A word she can read and see. `g` is its spelling split into sounds. */
export interface PhonicsWord {
  word: string
  g: string[]
  emoji: string
  level: number
}

const W = (level: number, emoji: string, ...g: string[]): PhonicsWord => ({ word: g.join(''), g, emoji, level })

export const WORDS: PhonicsWord[] = [
  // 1 · starts with a held sound
  W(1, '☀️', 's', 'u', 'n'), W(1, '🗺️', 'm', 'a', 'p'), W(1, '👨', 'm', 'a', 'n'), W(1, '🥅', 'n', 'e', 't'),
  W(1, '🥜', 'n', 'u', 't'), W(1, '🪭', 'f', 'a', 'n'), W(1, '🪵', 'l', 'o', 'g'), W(1, '👄', 'l', 'i', 'p'),
  W(1, '🐀', 'r', 'a', 't'), W(1, '🏃', 'r', 'u', 'n'), W(1, '😢', 's', 'a', 'd'), W(1, '🔴', 'r', 'e', 'd'),
  W(1, '🚐', 'v', 'a', 'n'), W(1, '🦵', 'l', 'e', 'g'), W(1, '🐏', 'r', 'a', 'm'),
  W(1, '🟫', 'm', 'a', 't'), W(1, '🟣', 'f', 'i', 'g'), W(1, '🧹', 'm', 'o', 'p'), W(1, '🍺', 'm', 'u', 'g'), W(1, '🟥', 'r', 'u', 'g'), W(1, '🤐', 'z', 'i', 'p'),
  // 2 · starts with a quick sound
  W(2, '🐱', 'c', 'a', 't'), W(2, '🧢', 'c', 'a', 'p'), W(2, '☕', 'c', 'u', 'p'), W(2, '🐶', 'd', 'o', 'g'),
  W(2, '🛏️', 'b', 'e', 'd'), W(2, '👜', 'b', 'a', 'g'), W(2, '🦇', 'b', 'a', 't'), W(2, '🚌', 'b', 'u', 's'),
  W(2, '🐛', 'b', 'u', 'g'), W(2, '📦', 'b', 'o', 'x'), W(2, '🐷', 'p', 'i', 'g'),
  W(2, '🖊️', 'p', 'e', 'n'), W(2, '📌', 'p', 'i', 'n'), W(2, '🔟', 't', 'e', 'n'), W(2, '🧒', 'k', 'i', 'd'),
  W(2, '🐔', 'h', 'e', 'n'), W(2, '👒', 'h', 'a', 't'), W(2, '🫂', 'h', 'u', 'g'), W(2, '🛖', 'h', 'u', 't'),
  W(2, '🦊', 'f', 'o', 'x'), W(2, '6️⃣', 's', 'i', 'x'), W(2, '🐂', 'o', 'x'), W(2, '🥚', 'e', 'gg'),
  W(2, '🕸️', 'w', 'e', 'b'), W(2, '👮', 'c', 'o', 'p'),
  W(2, '⛏️', 'd', 'i', 'g'), W(2, '💇', 'w', 'i', 'g'), W(2, '🍲', 'p', 'o', 't'), W(2, '🌀', 't', 'o', 'p'), W(2, '🍞', 'b', 'u', 'n'), W(2, '✈️', 'j', 'e', 't'),
  // 3 · two letters, one sound
  W(3, '🚢', 'sh', 'i', 'p'), W(3, '🏪', 'sh', 'o', 'p'), W(3, '🐟', 'f', 'i', 'sh'), W(3, '🍽️', 'd', 'i', 'sh'),
  W(3, '💵', 'c', 'a', 'sh'), W(3, '🐚', 'sh', 'e', 'll'), W(3, '🐤', 'ch', 'i', 'ck'), W(3, '✅', 'ch', 'e', 'ck'),
  W(3, '🛁', 'b', 'a', 'th'), W(3, '🦆', 'd', 'u', 'ck'), W(3, '🧦', 's', 'o', 'ck'), W(3, '🔒', 'l', 'o', 'ck'),
  W(3, '🪨', 'r', 'o', 'ck'), W(3, '💍', 'r', 'i', 'ng'), W(3, '🤴', 'k', 'i', 'ng'), W(3, '🪽', 'w', 'i', 'ng'),
  W(3, '🎤', 's', 'i', 'ng'),
  // 4 · sounds side by side
  W(4, '🐸', 'f', 'r', 'o', 'g'), W(4, '🚩', 'f', 'l', 'a', 'g'), W(4, '🥁', 'd', 'r', 'u', 'm'), W(4, '🦀', 'c', 'r', 'a', 'b'),
  W(4, '🛷', 's', 'l', 'e', 'd'), W(4, '🏊', 's', 'w', 'i', 'm'), W(4, '👏', 'c', 'l', 'a', 'p'), W(4, '🔌', 'p', 'l', 'u', 'g'),
  W(4, '🛑', 's', 't', 'o', 'p'), W(4, '💧', 'd', 'r', 'o', 'p'), W(4, '✋', 'h', 'a', 'n', 'd'), W(4, '🥛', 'm', 'i', 'l', 'k'),
  W(4, '🪺', 'n', 'e', 's', 't'), W(4, '⛺', 't', 'e', 'n', 't'), W(4, '🎁', 'g', 'i', 'f', 't'), W(4, '⛳', 'g', 'o', 'l', 'f'),
  W(4, '🐜', 'a', 'n', 't'), W(4, '🕰️', 'c', 'l', 'o', 'ck'), W(4, '😁', 'g', 'r', 'i', 'n'),
  // 5 · vowel teams
  W(5, '🌙', 'm', 'oo', 'n'), W(5, '👢', 'b', 'oo', 't'), W(5, '🥄', 's', 'p', 'oo', 'n'), W(5, '🌳', 't', 'r', 'ee'),
  W(5, '🐝', 'b', 'ee'), W(5, '🦶', 'f', 'ee', 't'), W(5, '🐑', 'sh', 'ee', 'p'), W(5, '🌧️', 'r', 'ai', 'n'),
  W(5, '🐌', 's', 'n', 'ai', 'l'), W(5, '📬', 'm', 'ai', 'l'), W(5, '🚆', 't', 'r', 'ai', 'n'), W(5, '⛵', 'b', 'oa', 't'),
  W(5, '🐐', 'g', 'oa', 't'), W(5, '🧥', 'c', 'oa', 't'), W(5, '🧼', 's', 'oa', 'p'), W(5, '🛣️', 'r', 'oa', 'd'),
  W(5, '🚗', 'c', 'ar'), W(5, '⭐', 's', 't', 'ar'), W(5, '🫙', 'j', 'ar'), W(5, '🦈', 'sh', 'ar', 'k'),
  W(5, '🍴', 'f', 'or', 'k'), W(5, '🌽', 'c', 'or', 'n'), W(5, '⛈️', 's', 't', 'or', 'm'), W(5, '🌃', 'n', 'igh', 't'),
  W(5, '💡', 'l', 'igh', 't'), W(5, '🐄', 'c', 'ow'), W(5, '🦉', 'ow', 'l'),
]

export const WORD_BY_TEXT: Record<string, PhonicsWord> = Object.fromEntries(WORDS.map((w) => [w.word, w]))

/**
 * The words taught by sight: the ones books need that cannot be sounded out
 * (said, was, the), or not with what she has met (he, go, my say their vowel's
 * name). This is the one list of them: the reading path's sight sets, the
 * Sight words deck, the sight engine and the story engine's memory all read
 * it, so a word is a sight word everywhere or nowhere. In the order the
 * little books first need them (a test holds it to SIGHT_SETS).
 */
export const SIGHT_WORDS = [
  'the', 'a', 'is', 'no', 'see', 'all', 'we', 'go', 'I', 'said', 'look', 'here', 'to', 'are', 'his', 'was',
  'he', 'she', 'me', 'my', 'you', 'of', 'they', 'there', 'some', 'come', 'one', 'love', 'what', 'have', 'do',
  'put', 'her', 'little', 'like', 'so', 'be',
]

const WRITTEN = new Map(SIGHT_WORDS.map((w) => [w.toLowerCase(), w]))

/** A word as it is written in a sentence a parent reads: memory keys are lower case, but I is always a capital. */
export const asWritten = (word: string) => WRITTEN.get(word.toLowerCase()) ?? word

/**
 * Short everyday words she will sound out (in, on, at, it, up, can, and),
 * never taught to memorise, that early books use before her blending level
 * reaches all of them ("and" has two sounds side by side). They have a
 * recorded voice, so a tap says them whole, and a book may use them at any
 * level. In her memory they are words (word:and), not sight words.
 */
export const EVERYDAY_WORDS = ['it', 'in', 'and', 'up', 'can', 'on', 'at']

/** Every word beyond the word bank with a recorded voice: said whole when tapped. */
export const SPOKEN_WORDS = [...SIGHT_WORDS, ...EVERYDAY_WORDS]

/** Split a word into the spellings the engines know, longest first. Null when it cannot be. */
export function splitGraphemes(word: string): string[] | null {
  const w = word.toLowerCase()
  const keys = Object.keys(GRAPHEMES).sort((a, b) => b.length - a.length)
  const out: string[] = []
  let i = 0
  while (i < w.length) {
    const hit = keys.find((k) => w.startsWith(k, i))
    if (!hit) return null
    out.push(hit)
    i += hit.length
  }
  return out
}

/**
 * The simplest level at which a word can be read by sounding it out, or null
 * when it needs spellings she has not met (a silent e, "ea", "y" as a vowel).
 * Sight words are not decodable by this measure; callers check them apart.
 */
export function decodableLevel(word: string): number | null {
  const known = WORD_BY_TEXT[word.toLowerCase()]
  if (known) return known.level
  const g = splitGraphemes(word)
  return g ? levelOfSpelling(g) : null
}

const SIGHT_SET = new Set(SIGHT_WORDS.map((w) => w.toLowerCase()))

/**
 * Every word she has tried to read by sounding out, from any game, book or
 * card, with the level it counts at: its spelling's level (decodableLevel),
 * so sat and mud from a book count the way map and sun from Blending do.
 * Sight words are not sounded out, so they never count here.
 */
export function blendedWords(memory: Record<string, ItemMemory>): Array<{ word: string; level: number; memory: ItemMemory }> {
  const out: Array<{ word: string; level: number; memory: ItemMemory }> = []
  for (const m of Object.values(memory)) {
    if (!m?.item.startsWith('word:') || !m.reps) continue
    const word = m.item.slice('word:'.length)
    const level = SIGHT_SET.has(word) ? null : decodableLevel(word)
    if (level) out.push({ word, level, memory: m })
  }
  return out
}

/**
 * How near she is to holding blending level n: the words of that level or
 * harder she holds (reading cat proves the sounds map needs), against the
 * fixed bar. A word she has shown stays held while she is away (holds); with
 * `fresh` only the words not fading count, which says a level has gone a
 * little rusty without taking it from her. `pending` is the words of that
 * level she read right in one sitting and not since missed: each counts once
 * she reads it right in another, so a parent who just watched her read
 * three words sees them on the way rather than a bare nought.
 */
export function blendLevelStanding(memory: Record<string, ItemMemory>, n: number, now: number, fresh = false): { held: number; needed: number; reached: boolean; pending: number } {
  const ok = fresh ? freshNow : holds
  const words = blendedWords(memory).filter((w) => w.level >= n)
  const held = words.filter((w) => ok(w.memory, now)).length
  const pending = words.filter((w) => !holds(w.memory, now) && w.memory.streak > 0 && (w.memory.rightDays ?? w.memory.right) >= 1).length
  return { held, needed: LEVEL_HOLD, reached: held >= LEVEL_HOLD, pending }
}

/**
 * The highest blending level she holds: LEVEL_HOLD words of it or above,
 * read well. The bar is a fixed count, never a share of the words she has
 * met, so practising more only brings it nearer. The reading path, the
 * progress line of every blend game and the blend engine (handed this at
 * launch) all read it, so they agree where she is.
 */
export function blendLevelFromMemory(memory: Record<string, ItemMemory>, now: number, fresh = false): number {
  let top = 0
  for (const { n } of LEVELS) if (blendLevelStanding(memory, n, now, fresh).reached) top = n
  return top
}

const VOWELS = new Set(['a', 'e', 'i', 'o', 'u'])
const TEAMS = new Set(['ee', 'oo', 'ai', 'oa', 'ar', 'or', 'igh', 'ow'])
const DIGRAPHS = new Set(['sh', 'ch', 'th', 'ng', 'ck', 'qu'])
/** A doubled letter or an x: one letter shape, but not a held start. */
const QUICK = new Set(['ff', 'll', 'ss', 'zz', 'gg', 'x'])

/** The level a spelling needs, from the spelling alone. */
export function levelOfSpelling(g: string[]): number | null {
  const vowel = (x: string) => VOWELS.has(x) || TEAMS.has(x)
  if (!g.some(vowel)) return null
  // A final e after a consonant is silent (cake, home), a final y is a
  // vowel (my, happy), and a lone final vowel is long (hi, go): all later lessons.
  const last = g[g.length - 1]!
  if (g.length > 2 && last === 'e' && !vowel(g[g.length - 2]!)) return null
  if (g.length > 1 && last === 'y') return null
  // A lone vowel ending a word says its name (hi, go, me), not its short sound.
  if (g.length > 1 && VOWELS.has(last) && !vowel(g[g.length - 2]!)) return null
  // The same silent e with an ending on it (likes, baked, homes).
  if (g.length > 3 && /^[sd]$/.test(last) && g[g.length - 2] === 'e' && !vowel(g[g.length - 3]!) && g.slice(0, -3).some(vowel)) return null
  // Two vowels side by side that are not a team she knows (ea, ou, ie).
  for (let i = 1; i < g.length; i++) if (VOWELS.has(g[i - 1]!) && VOWELS.has(g[i]!)) return null
  if (g.some((x) => TEAMS.has(x))) return 5
  for (let i = 1; i < g.length; i++) if (!vowel(g[i - 1]!) && !vowel(g[i]!)) return 4
  if (g.length > 3) return 4
  if (g.some((x) => DIGRAPHS.has(x))) return 3
  if (g.some((x) => QUICK.has(x))) return 2
  return PHONEME_BY_ID[GRAPHEMES[g[0]!]!]?.held || VOWELS.has(g[0]!) ? 1 : 2
}

/** A word family: the chunk after the first sound, and the bank words that share it. */
export interface WordFamily {
  rime: string[]
  words: { word: string; onset: string[] }[]
}

/**
 * Every family the word bank can fill (cat, hat, bat share "at"), keyed by the
 * chunk, with single-letter starts first. Only families with three or more
 * pictured words are worth a round.
 */
export function wordFamilies(min = 3): Record<string, WordFamily> {
  const vowelish = (g: string) => VOWELS.has(g) || TEAMS.has(g)
  const out: Record<string, WordFamily> = {}
  for (const w of WORDS) {
    const i = w.g.findIndex(vowelish)
    if (i < 1) continue
    const rime = w.g.slice(i)
    const key = rime.join('')
    ;(out[key] ??= { rime, words: [] }).words.push({ word: w.word, onset: w.g.slice(0, i) })
  }
  for (const key of Object.keys(out)) {
    if (out[key]!.words.length < min) delete out[key]
    else out[key]!.words.sort((a, b) => a.onset.length - b.onset.length)
  }
  return out
}

/**
 * The families a child at blending level `level` meets, as the families
 * engine fills them: only words of that level or easier, so at level 1 a
 * family is its held first sounds (man, fan, van) and two words are enough
 * to hear the rhyme; from level 2 a family needs three. Easiest first.
 */
export function familiesAtLevel(level: number): Record<string, WordFamily> {
  const upTo = Math.max(1, Math.min(LEVELS.length, Math.round(level) || 1))
  const lv = new Map(WORDS.map((w) => [w.word, w.level]))
  const out = Object.entries(wordFamilies(2))
    .map(([k, f]) => [k, { ...f, words: f.words.filter((w) => (lv.get(w.word) ?? 9) <= upTo) }] as const)
    .filter(([, f]) => f.words.length >= (upTo === 1 ? 2 : 3))
    .sort(([, a], [, b]) => b.words.length - a.words.length)
  return Object.fromEntries(out)
}
